import { v4 as uuidv4 } from 'uuid'
import UAParser from 'ua-parser-js'
import type { ClientLog, ClientLogMetadata } from '../../../shared/lib/ClientLog.no-deps'
import { omit } from '../../../shared/lib/functional/object.no-deps'
import { IDB_logs } from '../IDB/IDB.dexie'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import { Logg_createLogger } from './Logg.Logger'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'

const MAX_LOGS = 100

export type Logg_Log = ClientLog & {
    logId?: number
}

export async function Logg_sync() {
    // N.B. logs generated whilst uploading logs may or may not make it in the current batch
    // of logs to be uploaded. Otherwise they'll be uploaded next sync
    const logger = Logg_createLogger('Logg_sync')

    try {
        if (!ENV.isProduction && !ENV.isTest) {
            logger.log('not uploading logs whilst developing')
            return
        }

        const count = await IDB_logs().count()
        logger.log(`${count} logs to upload`)

        const metadata = await getMetadata()

        for (let i = 0; i < count; i += MAX_LOGS) {
            logger.log(`uploading logs from ${i} to ${Math.min(i + MAX_LOGS, count)}`)

            // Read from idb
            const logs = await IDB_logs().orderBy('timestamp').reverse().limit(MAX_LOGS).toArray()

            // Omit the local log id
            // exclude any big logs, these make the app run out of memory
            const logsToUpload = logs
                .map((l) => omit(l, 'logId'))
                .filter((log) => typeof log.message === 'string' && log.message.length < 500)

            const batchId = uuidv4() as UUID // create a uuid for this batch of logs
            // Upload
            await Serv_clientWithLogger(logger).addLogs(metadata, logsToUpload, batchId)

            // Delete local copies
            await IDB_logs().bulkDelete(logs.map((l) => l.logId))
            logger.log(`logs deleted`)
        }

        logger.log('success')
    } catch (error) {
        logger.error(error)
    }
}

/**
 * This is relying on parsing the user agent string which can be spoofed
 * So should not treat these as 100% gospel
 */
async function getMetadata(): Promise<ClientLogMetadata> {
    const { browser, engine, os, device } = new UAParser().getResult()

    return {
        appVersion: ENV.releaseVersion,
        deviceId: window.device?.uuid ?? null,
        browserName: browser?.name ?? null,
        browserVersion: browser?.version ?? null,
        engineName: engine?.name ?? null,
        osName: os?.name ?? null,
        osVersion: os?.version ?? null,
        deviceVendor: device?.vendor ?? null,
        deviceModel: device?.model ?? null,
    }
}
